import moment from 'moment';
// Utils
import { delay, getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from 'utils';
import { shorthandMonths } from 'utils/global';
import { findDefaultOrganisation, isActiveUser } from 'utils/user';
// Auth Context
import * as authActions from 'context/AuthContext/actions';
import * as authFlags from 'context/AuthContext/statusFlags';
// Services
import { getUser, patchUser } from 'services/productionV1/users';
import { postUserV2Token } from 'services/userV2/tokens';
import { postDispatcherV2Token } from 'services/dispatcherV2/tokens';
// Google Auth
// import { gapiAuth2Config, handleClientLoadAsPromise } from './googleConstants';
import { initFedCMLogin } from './googleConstants';
import { jwtDecode } from 'jwt-decode';

export async function setLastLogin(userId, dispatch) {
  moment.updateLocale('en', { monthsShort: shorthandMonths });
  try {
    dispatch(authActions.setAuthState(authFlags.PATCH_LAST_LOGIN));
    await patchUser({
      userId,
      data: {
        lastLoggedIn: moment().format('DD-MMM-YYYY'),
      },
    });
  } catch (error) {
    dispatch(authActions.setAuthState(authFlags.PATCH_LAST_LOGIN_ERROR));
  }
}

export async function authoriseEmployee(tokenResponse, dispatch) {
  const { empId } = tokenResponse;
  dispatch(authActions.setAuthState(authFlags.AUTHORISE_EMPLOYEE));
  const { errorMessage, response } = await getUser(empId);
  if (response && response.startDate && response.userId) {
    const { userId } = response;
    const defaultOrganisation = findDefaultOrganisation(response);
    const userIsActive = isActiveUser(response);
    if (!userIsActive) {
      dispatch(authActions.setAuthState(authFlags.AUTHORISE_EMPLOYEE_INACTIVE));
      return;
    }
    await setLastLogin(userId, dispatch);
    dispatch(authActions.setDefaultOrganisation(defaultOrganisation));
    dispatch(authActions.authEmployee(response));
    setSessionStorageItem({ key: 'employee', item: response });
    setSessionStorageItem({ key: 'defaultOrganisation', item: defaultOrganisation });
  }
  if (errorMessage) {
    dispatch(authActions.setAuthState(authFlags.AUTHORISE_EMPLOYEE_ERROR));
  }
}

export function authoriseEmployeeReturn(dispatch) {
  try {
    dispatch(authActions.setAuthState(authFlags.AUTHORISE_EMPLOYEE_RETURN));
    const employee = getSessionStorageItem({ item: 'employee' });
    const user = getSessionStorageItem({ item: 'user' });
    if (employee) {
      let defaultOrganisation = getSessionStorageItem({ item: 'defaultOrganisation' });
      if (defaultOrganisation) {
        dispatch(authActions.setDefaultOrganisation(defaultOrganisation));
      } else {
        defaultOrganisation = findDefaultOrganisation(employee);
        dispatch(authActions.setDefaultOrganisation(defaultOrganisation));
      }
      dispatch(authActions.setUser(user));
      dispatch(authActions.authEmployee(employee));
    }
  } catch (error) {
    dispatch(authActions.setAuthState(authFlags.AUTHORISE_EMPLOYEE_RETURN_ERROR));
  }
}

export async function setUserToken(token, dispatch) {
  dispatch(authActions.setAuthState(authFlags.POST_USER_TOKEN));
  const formData = new URLSearchParams();
  formData.append('grant_type', 'password');
  formData.append('id_token', token);
  const { errorMessage, response } = await postUserV2Token(formData);
  if (response) {
    sessionStorage.setItem('v2tkn', response.access_token);
    dispatch(authActions.setToken(response.access_token));
    await authoriseEmployee(response, dispatch);
  } 
  if (errorMessage) {
    dispatch(authActions.setAuthState(authFlags.POST_USER_TOKEN_ERROR));
  }
}

export async function setDispatcherToken(token, dispatch) {
  dispatch(authActions.setAuthState(authFlags.POST_USER_TOKEN));
  const formData = new URLSearchParams();
  formData.append('grant_type', 'password');
  formData.append('id_token', token);
  const { errorMessage, response } = await postDispatcherV2Token(formData);
  if (response) {
    sessionStorage.setItem('v2tkndispatcher', response.access_token);
    dispatch(authActions.setDispatcherToken(response.access_token));
    // await authoriseEmployee(response, dispatch);
  } 
  if (errorMessage) {
    dispatch(authActions.setAuthState(authFlags.POST_USER_TOKEN_ERROR));
  }
}
//fedcm changes
export async function onGoogleLoginSuccess(userDetails, idToken, dispatch) {
  dispatch(authActions.setAuthState(authFlags.GOOGLE_LOGIN));
  try {
    const user = {
      fullName: userDetails.name,
      givenName: userDetails.given_name,
      email: userDetails.email,
      avatar: userDetails.picture,
    };
    dispatch(authActions.setUser(user));
    setSessionStorageItem({ key: 'user', item: user });
    await setUserToken(idToken, dispatch);
    // await setDispatcherToken(googleUser.getAuthResponse().id_token, dispatch);
  } catch (error) {
    dispatch(authActions.setAuthState(authFlags.GOOGLE_LOGIN_ERROR));
  }
}

/* istanbul ignore next */ 
export async function initialiseGoogleLogin(dispatch) {
  try {
    initFedCMLogin((credentialResponse) => {
      const decoded = jwtDecode(credentialResponse.credential);

      onGoogleLoginSuccess(decoded, credentialResponse.credential, dispatch);
    })
  
  } catch (error) {
    console.error('Google Login Initialization Error:', error);
    dispatch(authActions.setAuthState(authFlags.GOOGLE_LOGIN_ERROR));
  }
}

export async function unauthoriseEmployee(dispatch) {
  dispatch(authActions.setAuthState(authFlags.UNAUTHORISE_EMPLOYEE));
  try {
    removeSessionStorageItem({ item: 'v2tkn' });
    removeSessionStorageItem({ item: 'defaultOrganisation' });
    removeSessionStorageItem({ item: 'employee' });
    removeSessionStorageItem({ item: 'readEventNotifications' });
    dispatch(authActions.setDefaultOrganisation({}));
    dispatch(authActions.setUser({}));
    dispatch(authActions.unauthEmployee());
  } catch (error) {
    dispatch(authActions.setAuthState(authFlags.UNAUTHORISE_EMPLOYEE_ERROR));
  }
}

export async function initialiseGoogleLogout(dispatch) {
  try {
    dispatch(authActions.setAuthState(authFlags.GOOGLE_LOGOUT));
    await unauthoriseEmployee(dispatch);
    await delay(2000);
    window.location.reload();
    window.location.pathname = '/';
  } catch (error) {
    dispatch(authActions.setAuthState(authFlags.GOOGLE_LOGOUT_ERROR));
  }
}